import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@wundo/ui';
import { formatDayAndMonth } from '@/components/agenda/meetingFormat.ts';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { fetchCompletedActionItems } from '@/lib/meetingPage.ts';
import { m } from '@/paraglide/messages.js';

export interface MeetingDoneActionItemsDialogProps {
  open: boolean;
  /** Whose agreements these are. Null while the meeting has no thread. */
  threadId: string | null;
  /**
   * Put one back on the open list. The writing is the page's own toggle, so a
   * tick made here and one made on the list are the same act.
   */
  onReopen: (itemId: string) => void;
  onClose: () => void;
}

/**
 * Everything ever ticked off on this conversation, newest first.
 *
 * This is the promise behind the checkbox: nothing a user closes is lost, it
 * only moves out of the way. Which is why each line carries the day it was
 * closed — an archive that cannot say when is a list of vague memories.
 *
 * Read when it is opened and not before. A conversation goes on for years and
 * most visits never ask for this, so it is not worth a byte of the page.
 */
export function MeetingDoneActionItemsDialog({
  open,
  threadId,
  onReopen,
  onClose,
}: MeetingDoneActionItemsDialogProps) {
  /*
   * What was put back while this was open. The list it came from is the list
   * of what is done, so a row that is open again does not belong in it — and
   * the page behind this dialog is already showing it on the open list.
   */
  const [reopened, setReopened] = useState<ReadonlySet<string>>(new Set());

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  const { data, isPending, isError } = useQuery({
    queryKey: ['meetingActionItemsDone', user?.id, threadId],
    queryFn: () => fetchCompletedActionItems(user!.id, threadId!),
    enabled: open && Boolean(user?.id) && Boolean(threadId),
  });

  const close = () => {
    setReopened(new Set());
    onClose();
  };

  const putBack = (itemId: string) => {
    setReopened((was) => new Set(was).add(itemId));
    onReopen(itemId);
  };

  const items = (data ?? []).filter((item) => !reopened.has(item.id));

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent size='md'>
        <DialogHeader>
          <DialogTitle>{m.meeting_prep_actions_done_title()}</DialogTitle>
        </DialogHeader>

        {/*
          The list carries the scroll, not the dialog: the title stays put and
          the button to leave stays reachable however long the archive grows.
        */}
        <div className='max-h-80 overflow-y-auto px-5 py-4'>
          {isError && (
            <p role='alert' className='text-sm text-danger'>
              {m.meeting_prep_actions_done_failed()}
            </p>
          )}

          {!isError && isPending && (
            <p className='text-sm text-ink-muted'>
              {m.meeting_prep_actions_done_loading()}
            </p>
          )}

          {!isError && !isPending && items.length === 0 && (
            <p className='text-sm text-ink-muted'>
              {m.meeting_prep_actions_done_empty()}
            </p>
          )}

          <ul className='flex flex-col'>
            {items.map((item) => (
              <li key={item.id} className='flex items-start gap-3 py-1.5'>
                {/*
                  Square and ticked, exactly as it was left on the open list —
                  and clicking it is the way back. Nothing else here is a
                  control: an archive is read, not edited.
                */}
                <Checkbox
                  aria-label={m.meeting_prep_actions_done_reopen({
                    text: item.text,
                  })}
                  checked
                  onCheckedChange={() => putBack(item.id)}
                />
                <div className='flex min-w-0 flex-col'>
                  <span className='text-base text-ink-muted line-through'>
                    {item.text}
                  </span>
                  <span className='text-xs text-ink-muted'>
                    {m.meeting_prep_actions_done_on({
                      date: formatDayAndMonth(new Date(item.done_at)),
                    })}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <DialogFooter>
          <Button size='sm' variant='quiet' onClick={close}>
            {m.meeting_prep_actions_done_close()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
